import { useEffect, useRef, useState } from "react";
import { storage } from "../../state/storage.js";
import { useT } from "../../i18n/use-t.js";
import type { TKey } from "../../i18n/strings.js";
import {
  createLorebook,
  createLorebookEntry,
  exportLorebookToJson,
  importLorebookFromJson,
  type Lorebook,
  type LorebookEntry,
  type LorebookPosition,
} from "@roqeet/core";

/**
 * 로어북(월드 인포) 관리(결정 #12). 로어북을 만들고 엔트리(키워드 + 내용 + 위치)를
 * 편집한다. 캐릭터 에디터에서 캐릭터에 연결하거나, 채팅 레벨로 붙일 수 있다.
 *
 * 스캔/주입 로직은 core(pipeline/lorebook-scan + build-prompt)에 이미 있으므로,
 * 여기서 만든 엔트리는 대화 시 자동으로 키워드 매칭되어 프롬프트에 주입된다.
 */
const POSITION_LABEL_KEYS: Record<LorebookPosition, TKey> = {
  "before-char": "lore.posBeforeChar",
  "after-char": "lore.posAfterChar",
  "before-an": "lore.posBeforeAn",
  "after-an": "lore.posAfterAn",
  "at-depth": "lore.posAtDepth",
};
const LOREBOOK_ENTRY_RENDER_WINDOW = 120;

export function LorebooksView() {
  const { t } = useT();
  const [lorebooks, setLorebooks] = useState<Lorebook[]>([]);
  const [active, setActive] = useState<Lorebook | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const reload = async () => {
    const list = await storage.lorebooks.list();
    setLorebooks(list);
    // 열려 있던 로어북 갱신.
    setActive((cur) => (cur ? (list.find((l) => l.id === cur.id) ?? null) : null));
  };
  useEffect(() => {
    void reload();
  }, []);

  const createNew = async () => {
    const book = createLorebook({ name: t("lore.newBook") });
    await storage.lorebooks.put(book);
    await reload();
    setActive(book);
  };

  const remove = async (book: Lorebook) => {
    if (!confirm(t("lore.deleteConfirm", { name: book.name }))) return;
    await storage.lorebooks.delete(book.id);
    if (active?.id === book.id) setActive(null);
    await reload();
  };

  // 단독 JSON으로 내보내기(결정 #6). ST/Risu world info와 호환되는 형태.
  const exportBook = async (book: Lorebook) => {
    const json = exportLorebookToJson(book);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = book.name.replace(/[^\w가-힣\- ]/g, "_").slice(0, 60) || "lorebook";
    a.href = url;
    a.download = `${safe}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // JSON 파일에서 로어북 가져오기(결정 #6).
  const onImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0) return;
    setNotice(null);
    let ok = 0;
    const errors: string[] = [];
    for (const file of files) {
      try {
        const book = importLorebookFromJson(await file.text());
        await storage.lorebooks.put(book);
        ok++;
      } catch (err) {
        errors.push(`${file.name}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    await reload();
    setNotice([t("lore.imported", { n: ok }), ...errors].join(" / ").slice(0, 300));
  };

  if (active) {
    return (
      <LorebookEditor
        lorebook={active}
        onBack={() => setActive(null)}
        onChange={reload}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        multiple
        className="hidden"
        onChange={onImport}
      />
      <div className="flex items-center justify-between gap-2 border-b border-line p-3">
        <span className="text-sm text-ink-soft">{t("lore.count", { count: lorebooks.length })}</span>
        <div className="flex gap-2">
          <button
            className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
            onClick={() => fileRef.current?.click()}
            title={t("lore.importTitle")}
          >
            {t("common.import")}
          </button>
          <button
            className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft"
            onClick={createNew}
          >
            {t("lore.newBook")}
          </button>
        </div>
      </div>

      {notice && (
        <div className="flex items-start justify-between gap-2 border-b border-line bg-bg-soft px-3 py-2 text-xs text-ink-soft">
          <span className="min-w-0 break-words">{notice}</span>
          <button className="shrink-0 text-ink-faint hover:text-ink" onClick={() => setNotice(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-3 scroll-touch">
        {lorebooks.length === 0 ? (
          <p className="px-1 py-8 text-center text-sm text-ink-faint">
            {t("lore.empty")}
          </p>
        ) : (
          <ul className="space-y-2">
            {lorebooks.map((book) => (
              <li
                key={book.id}
                className="flex items-center justify-between gap-2 rounded-xl border border-line bg-bg-soft px-3 py-3"
              >
                <button className="min-w-0 flex-1 text-left" onClick={() => setActive(book)}>
                  <div className="truncate text-sm text-ink">{book.name}</div>
                  <div className="truncate text-xs text-ink-faint">
                    {t("lore.entryCount", { count: book.entries.length })}
                  </div>
                </button>
                <button
                  className="shrink-0 rounded-lg border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
                  onClick={() => exportBook(book)}
                >
                  {t("common.export")}
                </button>
                <button
                  className="shrink-0 rounded-lg border border-line px-2 py-1 text-xs text-red-300 hover:bg-bg-raised"
                  onClick={() => remove(book)}
                >
                  {t("common.delete")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function LorebookContentField({
  value,
  placeholder,
  onCommit,
}: {
  value: string;
  placeholder: string;
  onCommit: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <textarea
      className="w-full resize-y rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
      rows={3}
      placeholder={placeholder}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        if (draft !== value) onCommit(draft);
      }}
    />
  );
}

function LorebookEditor({
  lorebook,
  onBack,
  onChange,
}: {
  lorebook: Lorebook;
  onBack: () => void;
  onChange: () => void | Promise<void>;
}) {
  const { t } = useT();
  const [name, setName] = useState(lorebook.name);
  const [entries, setEntries] = useState<LorebookEntry[]>(lorebook.entries);
  const [visibleCount, setVisibleCount] = useState(LOREBOOK_ENTRY_RENDER_WINDOW);
  const visibleEntries = entries.slice(0, visibleCount);
  const hiddenCount = Math.max(0, entries.length - visibleEntries.length);

  useEffect(() => {
    setVisibleCount(LOREBOOK_ENTRY_RENDER_WINDOW);
  }, [lorebook.id, entries.length]);

  const persist = async (nextEntries: LorebookEntry[], nextName = name) => {
    setEntries(nextEntries);
    await storage.lorebooks.put({ ...lorebook, name: nextName, entries: nextEntries });
    await onChange();
  };

  const addEntry = () => {
    void persist([...entries, createLorebookEntry({ content: "" })]);
  };

  const updateEntry = (id: string, patch: Partial<LorebookEntry>) => {
    void persist(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  const removeEntry = (id: string) => {
    void persist(entries.filter((e) => e.id !== id));
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3">
        <button
          aria-label={t("lore.backToList")}
          className="rounded p-2 text-ink-soft hover:bg-bg-raised"
          onClick={onBack}
        >
          ‹
        </button>
        <input
          className="flex-1 rounded-lg bg-transparent px-2 py-1 text-sm font-medium text-ink outline-none focus:bg-bg-soft"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => persist(entries)}
        />
        <button
          className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft"
          onClick={addEntry}
        >
          {t("lore.addEntry")}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3 scroll-touch">
        {entries.length === 0 ? (
          <p className="px-1 py-8 text-center text-sm text-ink-faint">
            {t("lore.entriesEmpty")}
          </p>
        ) : (
          <div className="mx-auto max-w-2xl space-y-3">
          <ul className="space-y-3">
            {visibleEntries.map((entry) => (
              <li key={entry.id} className="space-y-2 rounded-xl border border-line bg-bg-soft p-3">
                <div className="flex items-center gap-2">
                  <input
                    className="flex-1 rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
                    placeholder={t("lore.keysPlaceholder")}
                    defaultValue={entry.keys.join(", ")}
                    onBlur={(e) =>
                      updateEntry(entry.id, {
                        keys: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                      })
                    }
                  />
                  <button
                    className="shrink-0 rounded-lg border border-line px-2 py-1 text-xs text-red-300 hover:bg-bg-raised"
                    onClick={() => removeEntry(entry.id)}
                  >
                    {t("common.delete")}
                  </button>
                </div>
                <LorebookContentField
                  value={entry.content}
                  placeholder={t("lore.contentPlaceholder")}
                  onCommit={(content) => updateEntry(entry.id, { content })}
                />
                <div className="flex flex-wrap items-center gap-3 text-xs text-ink-soft">
                  <label className="flex items-center gap-1">
                    {t("lore.position")}
                    <select
                      className="rounded border border-line bg-bg-raised px-1 py-0.5 text-ink"
                      value={entry.position}
                      onChange={(e) =>
                        updateEntry(entry.id, { position: e.target.value as LorebookPosition })
                      }
                    >
                      {(Object.keys(POSITION_LABEL_KEYS) as LorebookPosition[]).map((p) => (
                        <option key={p} value={p}>
                          {t(POSITION_LABEL_KEYS[p])}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex items-center gap-1">
                    {t("lore.priority")}
                    <input
                      type="number"
                      className="w-16 rounded border border-line bg-bg-raised px-1 py-0.5 text-ink"
                      defaultValue={entry.insertionOrder}
                      onBlur={(e) =>
                        updateEntry(entry.id, { insertionOrder: Number(e.target.value) || 0 })
                      }
                    />
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={entry.constant}
                      onChange={(e) => updateEntry(entry.id, { constant: e.target.checked })}
                    />
                    {t("lore.alwaysInject")}
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={entry.disabled}
                      onChange={(e) => updateEntry(entry.id, { disabled: e.target.checked })}
                    />
                    {t("lore.entryDisabled")}
                  </label>
                </div>
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && (
            <div className="flex justify-center">
              <button
                className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft hover:bg-bg-raised"
                onClick={() => setVisibleCount((count) => count + LOREBOOK_ENTRY_RENDER_WINDOW)}
              >
                {t("chat.loadMore", { count: Math.min(LOREBOOK_ENTRY_RENDER_WINDOW, hiddenCount), hidden: hiddenCount })}
              </button>
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
}
